import { ApiError } from "@/api";
import type { Candidate, CandidateSignal } from "@/types/issuePipeline";

/** 오늘 / 어제 / 최근 7일 / 기간 지정 / 전체, all counted in KST days. */
export type DatePreset = "today" | "yesterday" | "week" | "custom" | "all";
export type DateFilter = { preset: DatePreset; from: string; to: string };
export type DateRange = { date_from?: string; date_to?: string };

export const DATE_PRESETS: Record<DatePreset, string> = {
  today: "오늘",
  yesterday: "어제",
  week: "최근 7일",
  custom: "기간 지정",
  all: "전체",
};

const KST_DAY = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit",
});

/** The KST calendar day of `now`, as YYYY-MM-DD. */
export function kstDay(now: Date) {
  return KST_DAY.format(now);
}

/** `day` moved by whole days; plain calendar arithmetic, so KST has no DST to trip on. */
export function shiftDay(day: string, days: number) {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** The `date_from`/`date_to` a filter sends; both inclusive, omitted when open. */
export function dateRange(filter: DateFilter, now: Date = new Date()): DateRange {
  const today = kstDay(now);
  switch (filter.preset) {
    case "today":
      return { date_from: today, date_to: today };
    case "yesterday": {
      const day = shiftDay(today, -1);
      return { date_from: day, date_to: day };
    }
    case "week":
      return { date_from: shiftDay(today, -6), date_to: today };
    case "custom": {
      // A reversed range is read the way the person meant it.
      const [from, to] = filter.from && filter.to && filter.from > filter.to
        ? [filter.to, filter.from] : [filter.from, filter.to];
      return { date_from: from || undefined, date_to: to || undefined };
    }
    case "all":
      return {};
  }
}

export type FeedEntry =
  | { kind: "candidate"; key: string; at: string; item: Candidate }
  | { kind: "signal"; key: string; at: string; item: CandidateSignal };

const newestFirst = (a: FeedEntry, b: FeedEntry) =>
  Date.parse(b.at) - Date.parse(a.at) || (a.key < b.key ? 1 : a.key > b.key ? -1 : 0);

/**
 * One list for the screen: candidates first, then the signals 1.1차 dropped,
 * each group newest first. A signal that already has a candidate (promoted
 * since the page loaded) is that candidate's row, so it is not listed twice.
 * `allSignals` is the 1차 신호 view: every signal, each showing its own result.
 */
export function mergeFeed(candidates: Candidate[], signals: CandidateSignal[], { allSignals = false } = {}): FeedEntry[] {
  const rows: FeedEntry[] = candidates.map((item) => (
    { kind: "candidate", key: item.candidate_id, at: item.created_at, item }
  ));
  const dropped: FeedEntry[] = signals
    .filter((item) => allSignals || !item.candidate_id)
    .map((item) => ({ kind: "signal", key: `signal-${signalKey(item)}`, at: item.detected_at, item }));
  return [...rows.sort(newestFirst), ...dropped.sort(newestFirst)];
}

/**
 * The signal list API is not on this server yet. Without the route,
 * `/candidates/signals` is either a plain 404 or is read as
 * `/candidates/{candidate_id}` and refused as a malformed UUID.
 */
export function signalsUnavailable(error: unknown) {
  if (!(error instanceof ApiError)) return false;
  if (error.status === 404) return true;
  return error.status === 422 && error.code === "REQUEST_VALIDATION" && error.message.includes("candidate_id");
}

/** One signal row: the list keeps one per origin, so the same message id may come from dev and prod. */
export function signalKey(signal: { source_environment: string; signal_message_id: string }) {
  return `${signal.source_environment}:${signal.signal_message_id}`;
}
